import { useState, useEffect, useCallback, useRef } from 'react';
import timelineAPI from '../services/timelineAPI';

/**
 * useTimeline Hook - Centralized timeline state management
 * 
 * This hook provides a React interface to the Timeline API service,
 * managing state synchronization and providing callbacks for UI events.
 */
export const useTimeline = (initialClips = []) => {
  // Core timeline state
  const [clips, setClips] = useState([]);
  const [selectedClipIds, setSelectedClipIds] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Undo/redo state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  // Player state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Internal refs
  const updateTimeoutRef = useRef(null);
  const playbackIntervalRef = useRef(null);

  // Initialize timeline with clips
  useEffect(() => {
    if (initialClips && initialClips.length > 0) {
      timelineAPI.clear();
      initialClips.forEach(clip => {
        timelineAPI.addClip(clip, clip.startTime);
      });
    }
  }, [initialClips]);

  // Subscribe to timeline API events
  useEffect(() => {
    const unsubscribe = timelineAPI.subscribe((event, data) => {
      switch (event) {
        case 'clipAdded':
        case 'clipRemoved':
        case 'clipMoved':
        case 'clipTrimmed':
        case 'clipSplit':
        case 'timelineCleared':
        case 'selectedClipsModified':
          // Debounced update to avoid rapid re-renders
          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }
          updateTimeoutRef.current = setTimeout(() => {
            setClips([...timelineAPI.getClips()]);
            setDuration(timelineAPI.getDuration());
            setCanUndo(timelineAPI.canUndo());
            setCanRedo(timelineAPI.canRedo());
          }, 16); // 60fps update rate
          break;
          
        case 'clipsSelected':
          setSelectedClipIds([...data.clipIds]);
          break;
          
        case 'aspectRatioChanged':
          setAspectRatio(data.ratio);
          break;
          
        case 'undoRedo':
          setClips([...timelineAPI.getClips()]);
          setSelectedClipIds([...timelineAPI.selectedClipIds]);
          setDuration(timelineAPI.getDuration());
          setAspectRatio(timelineAPI.getAspectRatio());
          setCanUndo(timelineAPI.canUndo());
          setCanRedo(timelineAPI.canRedo());
          break;
      }
    });

    // Initial state sync
    setClips([...timelineAPI.getClips()]);
    setSelectedClipIds([...timelineAPI.selectedClipIds]);
    setDuration(timelineAPI.getDuration());
    setAspectRatio(timelineAPI.getAspectRatio());
    setCanUndo(timelineAPI.canUndo());
    setCanRedo(timelineAPI.canRedo());

    return () => {
      unsubscribe();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Playback management
  useEffect(() => {
    if (isPlaying) {
      playbackIntervalRef.current = setInterval(() => {
        setCurrentTime(prevTime => {
          const newTime = prevTime + 0.1; // 100ms increments
          if (newTime >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return newTime;
        });
      }, 100);
    } else {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
    }

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, [isPlaying, duration]);

  // Timeline manipulation callbacks
  const addClip = useCallback((clip, startTime) => {
    try {
      return timelineAPI.addClip(clip, startTime);
    } catch (err) {
      setError(`Failed to add clip: ${err.message}`);
      return null;
    }
  }, []);

  const moveClip = useCallback((clipId, newTime) => {
    try {
      return timelineAPI.moveClip(clipId, newTime);
    } catch (err) {
      setError(`Failed to move clip: ${err.message}`);
      return false;
    }
  }, []);

  const removeClip = useCallback((clipId) => {
    try {
      return timelineAPI.removeClip(clipId);
    } catch (err) {
      setError(`Failed to remove clip: ${err.message}`);
      return false;
    }
  }, []);

  const trimClip = useCallback((clipId, newStart, newEnd) => {
    try {
      return timelineAPI.trimClip(clipId, newStart, newEnd);
    } catch (err) {
      setError(`Failed to trim clip: ${err.message}`);
      return false;
    }
  }, []);

  const splitClip = useCallback((clipId, splitTime) => {
    try {
      return timelineAPI.splitClip(clipId, splitTime);
    } catch (err) {
      setError(`Failed to split clip: ${err.message}`);
      return null;
    }
  }, []);

  const selectClips = useCallback((clipIds) => {
    timelineAPI.selectClips(clipIds);
  }, []);

  const clearTimeline = useCallback(() => {
    timelineAPI.clear();
    setCurrentTime(0);
    setIsPlaying(false);
  }, []);

  const changeAspectRatio = useCallback((ratio) => {
    timelineAPI.setAspectRatio(ratio);
  }, []);

  // Undo/redo callbacks
  const undo = useCallback(() => {
    timelineAPI.undo();
  }, []);

  const redo = useCallback(() => {
    timelineAPI.redo();
  }, []);

  // Playback controls
  const play = useCallback(() => {
    if (duration > 0) {
      setIsPlaying(true);
      setError(null);
    }
  }, [duration]);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const seek = useCallback((time) => {
    const clampedTime = Math.max(0, Math.min(time, duration));
    setCurrentTime(clampedTime);
    setError(null);
  }, [duration]);

  const skipToStart = useCallback(() => {
    setCurrentTime(0);
    setIsPlaying(false);
  }, []);

  const skipToEnd = useCallback(() => {
    setCurrentTime(duration);
    setIsPlaying(false);
  }, [duration]);

  // Advanced timeline operations
  const findClipsByTag = useCallback((tagName) => {
    return timelineAPI.findClipsByTag(tagName);
  }, []);

  const getCompositionAtTime = useCallback((time) => {
    return timelineAPI.getCompositionAtTime(time);
  }, []);

  const analyzeTimeline = useCallback(() => {
    return timelineAPI.analyzeTimeline();
  }, []);

  const getClipsInRange = useCallback((startTime, endTime) => {
    return timelineAPI.getClipsInRange(startTime, endTime);
  }, []);

  // Batch operations for AI
  const batchOperations = useCallback((operations) => {
    setIsLoading(true);
    try {
      operations.forEach(op => {
        switch (op.type) {
          case 'addClip':
            timelineAPI.addClip(op.clip, op.startTime);
            break;
          case 'moveClip':
            timelineAPI.moveClip(op.clipId, op.newTime);
            break;
          case 'removeClip':
            timelineAPI.removeClip(op.clipId);
            break;
          case 'trimClip':
            timelineAPI.trimClip(op.clipId, op.newStart, op.newEnd);
            break;
          case 'splitClip':
            timelineAPI.splitClip(op.clipId, op.splitTime);
            break;
          default:
            console.warn(`Unknown operation type: ${op.type}`);
        }
      });
    } catch (err) {
      setError(`Batch operation failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Export timeline state for persistence
  const exportTimelineState = useCallback(() => {
    return {
      clips: clips.map(clip => ({
        ...clip,
        // Remove UI-specific properties for clean export
        selected: undefined
      })),
      duration,
      aspectRatio,
      currentTime
    };
  }, [clips, duration, aspectRatio, currentTime]);

  // Import timeline state
  const importTimelineState = useCallback((state) => {
    try {
      timelineAPI.clear();
      if (state.clips) {
        state.clips.forEach(clip => {
          timelineAPI.addClip(clip, clip.startTime);
        });
      }
      if (state.aspectRatio) {
        timelineAPI.setAspectRatio(state.aspectRatio);
      }
      if (typeof state.currentTime === 'number') {
        setCurrentTime(state.currentTime);
      }
      setError(null);
    } catch (err) {
      setError(`Failed to import timeline: ${err.message}`);
    }
  }, []);

  // Clear any errors
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    clips,
    selectedClipIds,
    currentTime,
    duration,
    aspectRatio,
    isPlaying,
    isLoading,
    error,
    canUndo,
    canRedo,

    // Timeline manipulation
    addClip,
    moveClip,
    removeClip,
    trimClip,
    splitClip,
    selectClips,
    clearTimeline,
    changeAspectRatio,

    // Playback controls
    play,
    pause,
    seek,
    skipToStart,
    skipToEnd,

    // Undo/redo
    undo,
    redo,

    // Advanced operations
    findClipsByTag,
    getCompositionAtTime,
    analyzeTimeline,
    getClipsInRange,
    batchOperations,

    // State management
    exportTimelineState,
    importTimelineState,
    clearError,

    // Direct API access for AI code generation
    timelineAPI
  };
};